import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import { toast } from 'react-toastify';
import { X, Calendar, Clock, MapPin, Award, Users, Tag, FileText, Activity, Image } from 'lucide-react';
import { Event } from '../types';

interface EditEventModalProps {
  event: Event;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const EditEventModal: React.FC<EditEventModalProps> = ({ event, isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_date: '',
    event_time: '',
    location: '',
    category: 'workshop',
    points: 10,
    capacity: '',
    status: 'upcoming',
    certificate_template: '',
  });
  const [brochureFile, setBrochureFile] = useState<File | null>(null);
  const [brochurePreview, setBrochurePreview] = useState<string>('');
  const [existingBrochure, setExistingBrochure] = useState<string>('');

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title || '',
        description: event.description || '',
        event_date: event.event_date ? event.event_date.split('T')[0] : '',
        event_time: event.event_time || '',
        location: event.location || '',
        category: event.category || 'workshop',
        points: event.points || 10,
        capacity: event.capacity?.toString() || '',
        status: event.status || 'upcoming',
        certificate_template: event.certificate_template || '',
      });
      setExistingBrochure(event.brochure_url || event.brochureUrl || '');
      setBrochureFile(null);
      setBrochurePreview('');
    }
  }, [event]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'points' ? parseInt(value) || 0 : value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleBrochureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload a valid image file (JPG, PNG, or WEBP)');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      setBrochureFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setBrochurePreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Clear error
      if (errors.brochure) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.brochure;
          return newErrors;
        });
      }
    }
  };

  const removeBrochure = () => {
    setBrochureFile(null);
    setBrochurePreview('');
    setExistingBrochure('');
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length < 3) {
      newErrors.title = 'Title must be at least 3 characters';
    }

    if (!formData.event_date) {
      newErrors.event_date = 'Event date is required';
    }

    if (formData.points < 0 || formData.points > 1000) {
      newErrors.points = 'Points must be between 0 and 1000';
    }

    if (formData.capacity && parseInt(formData.capacity) < 1) {
      newErrors.capacity = 'Capacity must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title.trim());
      if (formData.description.trim()) {
        formDataToSend.append('description', formData.description.trim());
      }
      formDataToSend.append('event_date', formData.event_date);
      if (formData.event_time) {
        formDataToSend.append('event_time', formData.event_time);
      }
      if (formData.location.trim()) {
        formDataToSend.append('location', formData.location.trim());
      }
      formDataToSend.append('category', formData.category);
      formDataToSend.append('points', formData.points.toString());
      formDataToSend.append('status', formData.status);
      if (formData.capacity) {
        formDataToSend.append('capacity', formData.capacity);
      }
      if (formData.certificate_template) {
        formDataToSend.append('certificate_template', formData.certificate_template);
      }
      if (brochureFile) {
        formDataToSend.append('brochure', brochureFile);
      }

      await axios.put(`/v1/events/${event.id}`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Event updated successfully!');
      onSuccess();
    } catch (error: any) {
      console.error('Failed to update event:', error);
      const errorMessage = error.response?.data?.error?.message || 'Failed to update event';
      toast.error(errorMessage);

      // Handle validation errors from backend
      if (error.response?.data?.errors) {
        const backendErrors: { [key: string]: string } = {};
        error.response.data.errors.forEach((err: any) => {
          backendErrors[err.path] = err.msg;
        });
        setErrors(backendErrors);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-secondary-200 px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-secondary-900">Edit Event</h2>
            <p className="text-sm text-secondary-600">Update event details</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
            disabled={loading}
          >
            <X size={24} className="text-secondary-600" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-5">
            {/* Title */}
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                <FileText size={16} className="inline mr-1" />
                Event Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter event title"
                className={`input w-full ${errors.title ? 'border-red-500 focus:border-red-500' : ''}`}
                disabled={loading}
              />
              {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                <FileText size={16} className="inline mr-1" />
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter event description"
                rows={3}
                className="input w-full resize-none"
                disabled={loading}
              />
            </div>

            {/* Date and Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  <Calendar size={16} className="inline mr-1" />
                  Event Date *
                </label>
                <input
                  type="date"
                  name="event_date"
                  value={formData.event_date}
                  onChange={handleChange}
                  className={`input w-full ${errors.event_date ? 'border-red-500 focus:border-red-500' : ''}`}
                  disabled={loading}
                />
                {errors.event_date && <p className="mt-1 text-sm text-red-600">{errors.event_date}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  <Clock size={16} className="inline mr-1" />
                  Event Time
                </label>
                <input
                  type="time"
                  name="event_time"
                  value={formData.event_time}
                  onChange={handleChange}
                  className="input w-full"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                <MapPin size={16} className="inline mr-1" />
                Location
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="Enter event location"
                className="input w-full"
                disabled={loading}
              />
            </div>

            {/* Category and Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  <Tag size={16} className="inline mr-1" />
                  Category *
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="input w-full"
                  disabled={loading}
                >
                  <option value="workshop">Workshop</option>
                  <option value="seminar">Seminar</option>
                  <option value="social">Social</option>
                  <option value="sports">Sports</option>
                  <option value="community_service">Community Service</option>
                  <option value="fundraising">Fundraising</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  <Activity size={16} className="inline mr-1" />
                  Status *
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="input w-full"
                  disabled={loading}
                >
                  <option value="upcoming">Upcoming</option>
                  <option value="ongoing">Ongoing</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            {/* Certificate Template */}
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                <Award size={16} className="inline mr-1" />
                Certificate Template (Optional)
              </label>
              <select
                name="certificate_template"
                value={formData.certificate_template}
                onChange={handleChange}
                className="input w-full"
                disabled={loading}
              >
                <option value="">No Certificate</option>
                <option value="Beach Cleanup">Beach Cleanup</option>
                <option value="World Biggest Beach Cleanup">World Biggest Beach Cleanup</option>
                <option value="CPR & FIRST RESPONSE WORKSHOP">CPR & FIRST RESPONSE WORKSHOP</option>
                <option value="Fire Safety">Fire Safety</option>
                <option value="Medical Camp">Medical Camp</option>
                <option value="Plantation Drive">Plantation Drive</option>
                <option value="Park Cleanup &amp; Restoration">Park Cleanup &amp; Restoration</option>
              </select>
              <p className="text-xs text-secondary-500 mt-1">
                Select a certificate template that will be awarded to participants
              </p>
            </div>

            {/* Points and Capacity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  <Award size={16} className="inline mr-1" />
                  Points *
                </label>
                <input
                  type="number"
                  name="points"
                  value={formData.points}
                  onChange={handleChange}
                  min="0"
                  max="1000"
                  className={`input w-full ${errors.points ? 'border-red-500 focus:border-red-500' : ''}`}
                  disabled={loading}
                />
                {errors.points && <p className="mt-1 text-sm text-red-600">{errors.points}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  <Users size={16} className="inline mr-1" />
                  Capacity (Optional)
                </label>
                <input
                  type="number"
                  name="capacity"
                  value={formData.capacity}
                  onChange={handleChange}
                  min="1"
                  placeholder="Max attendees"
                  className={`input w-full ${errors.capacity ? 'border-red-500 focus:border-red-500' : ''}`}
                  disabled={loading}
                />
                {errors.capacity && <p className="mt-1 text-sm text-red-600">{errors.capacity}</p>}
              </div>
            </div>

            {/* Event Brochure */}
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                <Image size={16} className="inline mr-1" />
                Event Brochure (Optional)
              </label>
              <div className="space-y-3">
                {brochurePreview || existingBrochure ? (
                  <div className="relative">
                    <img
                      src={brochurePreview || existingBrochure}
                      alt="Brochure preview"
                      className="w-full h-48 object-cover rounded-lg border-2 border-secondary-200"
                    />
                    <button
                      type="button"
                      onClick={removeBrochure}
                      className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                      disabled={loading}
                    >
                      <X size={16} />
                    </button>
                    {!brochurePreview && existingBrochure && (
                      <div className="absolute bottom-2 left-2 px-3 py-1 bg-blue-500 text-white text-xs rounded-full">
                        Current Brochure
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-secondary-300 rounded-lg p-6 text-center hover:border-primary-500 transition-colors">
                    <Image size={48} className="mx-auto mb-3 text-secondary-400" />
                    <label className="cursor-pointer">
                      <span className="text-sm text-secondary-600">
                        Click to upload or drag and drop
                      </span>
                      <p className="text-xs text-secondary-500 mt-1">
                        PNG, JPG or WEBP (MAX. 5MB)
                      </p>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleBrochureChange}
                        className="hidden"
                        disabled={loading}
                      />
                    </label>
                  </div>
                )}
              </div>
              {errors.brochure && <p className="mt-1 text-sm text-red-600">{errors.brochure}</p>}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-secondary-200">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner mr-2"></span>
                  Updating...
                </>
              ) : (
                'Update Event'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEventModal;
